import { normalizeCountryCode } from "util/countryDisplay";
import { formatDeparture } from "util/departureDisplay";

export function resolveZipcodeLngLat(row) {
  if (!row) {
    return null;
  }
  const lat = Number(row.latitude);
  const lng = Number(row.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)) {
    return { latitude: lat, longitude: lng };
  }
  const raw = row.coordinates;
  if (Array.isArray(raw) && raw.length >= 2) {
    const lng2 = Number(raw[0]);
    const lat2 = Number(raw[1]);
    if (Number.isFinite(lat2) && Number.isFinite(lng2) && !(lat2 === 0 && lng2 === 0)) {
      return { latitude: lat2, longitude: lng2 };
    }
  }
  if (raw?.coordinates && Array.isArray(raw.coordinates) && raw.coordinates.length >= 2) {
    const lng2 = Number(raw.coordinates[0]);
    const lat2 = Number(raw.coordinates[1]);
    if (Number.isFinite(lat2) && Number.isFinite(lng2) && !(lat2 === 0 && lng2 === 0)) {
      return { latitude: lat2, longitude: lng2 };
    }
  }
  if (raw && typeof raw === "object" && raw.x != null && raw.y != null) {
    const lng2 = Number(raw.x);
    const lat2 = Number(raw.y);
    if (Number.isFinite(lat2) && Number.isFinite(lng2) && !(lat2 === 0 && lng2 === 0)) {
      return { latitude: lat2, longitude: lng2 };
    }
  }
  if (typeof raw === "string") {
    const match = raw.match(/POINT\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/i);
    if (match) {
      const lng3 = Number(match[1]);
      const lat3 = Number(match[2]);
      if (Number.isFinite(lat3) && Number.isFinite(lng3) && !(lat3 === 0 && lng3 === 0)) {
        return { latitude: lat3, longitude: lng3 };
      }
    }
  }
  return null;
}

export function findZipcodeRowWithCoordinates(rows, country = null) {
  const countryCode = country != null ? normalizeCountryCode(country) : null;
  for (const row of rows || []) {
    if (countryCode != null && normalizeCountryCode(row?.country) !== countryCode) {
      continue;
    }
    if (resolveZipcodeLngLat(row) != null) {
      return row;
    }
  }
  return null;
}

export function zipcodeTourGroupKey(zipcode, tourId, country) {
  const z = String(zipcode ?? "").trim();
  const tid = tourId == null ? "" : String(tourId);
  const c = normalizeCountryCode(country);
  return `${z}\0${tid}\0${c}`;
}

export function zipcodeSupplierGroupKey(zipcode, supplierId, country) {
  const z = String(zipcode ?? "").trim();
  const sid = supplierId == null ? "" : String(supplierId);
  const c = normalizeCountryCode(country);
  return `${z}\0${sid}\0${c}`;
}

export function groupZipcodesForZipcodeTable(zipcodes, t) {
  const map = new Map();
  for (const raw of zipcodes || []) {
    const z = String(raw.zipcode ?? "").trim();
    if (!z) {
      continue;
    }
    const country = normalizeCountryCode(raw.country);

    const assignments = [];
    if (raw.ZipcodeDeliveryTimings && raw.ZipcodeDeliveryTimings.length > 0) {
      for (const zdt of raw.ZipcodeDeliveryTimings) {
        const tdt = zdt.ToursDeliveryTiming;
        if (tdt) {
          assignments.push(`${tdt.tour_id}:${tdt.delivery_timing || ""}`);
        }
      }
    } else if (raw.tour_id) {
      assignments.push(`${raw.tour_id}:${raw.departure || ""}`);
    }
    assignments.sort();
    const tourAssignmentsStr = assignments.join(",");

    const key = `${z}\0${country}\0${tourAssignmentsStr}`;
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key).push(raw);
  }

  const sortedKeys = [...map.keys()].sort((a, b) => {
    const [za, ca] = a.split("\0");
    const [zb, cb] = b.split("\0");
    const zipCmp = String(zb).localeCompare(String(za));
    if (zipCmp !== 0) {
      return zipCmp;
    }
    return String(ca).localeCompare(String(cb));
  });

  const out = sortedKeys.map((key) => {
    const rows = map.get(key);
    const z = String(rows[0]?.zipcode ?? "").trim();
    const country = normalizeCountryCode(rows[0]?.country);

    const supplierEntries = [];
    const seenSuppliers = new Set();
    for (const r of rows) {
      const sid = r.supplier_id;
      if (sid != null && !seenSuppliers.has(sid)) {
        seenSuppliers.add(sid);
        supplierEntries.push({ id: sid, name: r.Supplier?.name ?? String(sid) });
      }
    }
    supplierEntries.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    const supplierIds = supplierEntries.map((entry) => entry.id);
    const supplierNames = supplierEntries.map((entry) => entry.name);

    // Group departures by tour name and tour ID
    const tourMap = new Map();
    const tourIdMap = new Map();
    for (const r of rows) {
      if (r.ZipcodeDeliveryTimings && r.ZipcodeDeliveryTimings.length > 0) {
        for (const zdt of r.ZipcodeDeliveryTimings) {
          const tdt = zdt.ToursDeliveryTiming;
          if (tdt) {
            const tourName = tdt.Tour?.name || "No Tour";
            const tid = tdt.tour_id != null ? Number(tdt.tour_id) : null;
            if (!tourMap.has(tourName)) {
              tourMap.set(tourName, new Set());
            }
            if (tid != null && !tourIdMap.has(tid)) {
              tourIdMap.set(tid, { tour_id: tid, name: tourName, departures: new Set() });
            }
            if (tdt.delivery_timing) {
              tourMap.get(tourName).add(tdt.delivery_timing);
              if (tid != null) {
                tourIdMap.get(tid).departures.add(tdt.delivery_timing);
              }
            }
          }
        }
      } else {
        const tourName = r.Tour?.name || "No Tour";
        const tid = r.tour_id != null ? Number(r.tour_id) : null;
        if (!tourMap.has(tourName)) {
          tourMap.set(tourName, new Set());
        }
        if (tid != null && !tourIdMap.has(tid)) {
          tourIdMap.set(tid, { tour_id: tid, name: tourName, departures: new Set() });
        }
        if (r.departure) {
          tourMap.get(tourName).add(r.departure);
          if (tid != null) {
            tourIdMap.get(tid).departures.add(r.departure);
          }
        }
      }
    }
    const tourEntries = [...tourMap.entries()].map(([name, deps]) => ({
      name,
      departures: [...deps],
    }));
    const tourMappings = [...tourIdMap.values()].map((m) => ({
      tour_id: m.tour_id,
      name: m.name,
      departures: [...m.departures],
    }));
    const tourLabel = tourEntries
      .map(({ name, departures }) => {
        if (departures.length > 0) {
          return `${name} (${departures.map((d) => formatDeparture(d, t)).join(", ")})`;
        }
        return name;
      })
      .join(", ");

    const rowIds = rows.map((r) => r.id).sort((a, b) => a - b);
    const primaryEditId = Math.min(...rowIds);
    const coordRow = findZipcodeRowWithCoordinates(rows, country);
    const resolved = coordRow ? resolveZipcodeLngLat(coordRow) : null;

    const nestedTourId = (rows[0]?.ZipcodeDeliveryTimings || [])
      .map((zdt) => zdt.ToursDeliveryTiming?.tour_id)
      .filter((tid) => tid != null)[0];
    const resolvedTourId = nestedTourId || rows[0]?.tour_id || null;

    const targets = rows.map((r) => ({
      zipcode: z,
      tour_id: r.tour_id,
      country: normalizeCountryCode(r.country),
    }));

    return {
      id: `grp-${z}-${country}-${supplierIds.join("-")}`,
      zipcode: z,
      country,
      supplierLabel: supplierNames.join(", "),
      tourLabel,
      _tourEntries: tourEntries,
      _tourMappings: tourMappings,
      Supplier: { name: supplierNames.join(", ") },
      Tour: { name: tourLabel },
      _rowIds: rowIds,
      _supplierIds: supplierIds,
      _supplierNames: supplierNames,
      _tourId: resolvedTourId,
      _country: country,
      _primaryEditId: primaryEditId,
      _supplierLocationIds: [...new Set(rows.map((r) => r.supplier_location_id).filter((x) => x != null))],
      latitude: resolved?.latitude ?? null,
      longitude: resolved?.longitude ?? null,
      hasCoordinates: resolved != null,
      _targets: targets,
    };
  });
  return out;
}

export function rowToZipcodeTourTarget(row) {
  const zipcode = String(row?.zipcode ?? "").trim();
  const tour_id = row?._tourId ?? row?.tour_id;
  const country = normalizeCountryCode(row?._country ?? row?.country);
  if (!zipcode || tour_id == null) {
    return null;
  }
  return { zipcode, tour_id: Number(tour_id), country };
}

export function uniqueZipcodeTourTargets(rows) {
  const seen = new Set();
  const out = [];
  for (const row of rows || []) {
    const rowTargets = row._targets || (rowToZipcodeTourTarget(row) ? [rowToZipcodeTourTarget(row)] : []);
    for (const t of rowTargets) {
      if (!t) continue;
      const key = `${t.zipcode}\0${t.tour_id}\0${t.country}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      out.push(t);
    }
  }
  return out;
}

export function supplierIdsKey(ids) {
  return [...ids].sort((a, b) => a - b).join(",");
}

export function selectionHasUniformSuppliers(rows) {
  if (!rows?.length) {
    return false;
  }
  const key = supplierIdsKey(rows[0]._supplierIds || []);
  return rows.every((r) => supplierIdsKey(r._supplierIds || []) === key);
}

export function selectionHasUniformTour(rows) {
  if (!rows?.length) {
    return false;
  }
  const tourId = rows[0]._tourId;
  if (tourId == null) {
    return false;
  }
  return rows.every((r) => r._tourId === tourId);
}

export const zipcodeTableSelectionRefs = {
  pinnedRowIds: new Set(),
  groupedData: [],
};

export function mergePinnedZipcodeRenderData(fullData, renderData, pinnedRowIds) {
  if (!pinnedRowIds?.size || !Array.isArray(renderData)) {
    return renderData;
  }
  const pinned = [];
  const unpinned = [];
  for (const row of renderData) {
    if (pinnedRowIds.has(row.id) || row.tableData?.checked) {
      pinned.push(row);
    } else {
      unpinned.push(row);
    }
  }
  return [...pinned, ...unpinned];
}

export function pinSelectedZipcodeTableRowFilter(selectionRefs) {
  return (filterValue, row, columnDef) => {
    if (!filterValue) {
      return true;
    }
    const raw = row[columnDef.field];
    if (raw == null || raw === "") {
      return false;
    }
    return String(raw).toUpperCase().includes(String(filterValue).trim().toUpperCase());
  };
}

